import InputField from "@components/form/InputField";
import SelectField from "@components/form/SelectField";
import PhoneField from "@components/form/PhoneField";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllEmployees } from "@services/employee";
import { getAllProviders } from "@services/provider";
import { validatePatientForm } from "@utils/validators";
import { sanitizeFormValues } from "@utils/sanitize";
import StepHeader from "@components/form-builder/StepHeader";

export default function PatientForm({ onSubmit, patient }) {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [providerOptions, setProviderOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [form, setForm] = useState(
    patient || {
      id: null,
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      insurance_provider_id: null,
      insurance_provider: "",
      policy_number: "",
      assigned_doctor_id: null,
      assigned_doctor: "",
    }
  );
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const providers = await getAllProviders();
        const employees = await getAllEmployees();

        setProviderOptions(providers);
        setDoctorOptions(employees);
      } catch (err) {
        console.error("Failed to load dropdown metadata", err);
      }
    };
    loadMetadata();
  }, []);

  const handleProviderChange = (e) => {
    const selectedId = e.target.value;
    const selected = providerOptions.find((opt) => opt.value === selectedId);
    setForm({
      ...form,
      insurance_provider_id: selectedId,
      insurance_provider: selected?.label || "",
    });
    setErrors({ ...errors, insurance_provider_id: "" });
  };

  const handleDoctorChange = (e) => {
    const selectedId = e.target.value;
    const selected = doctorOptions.find((opt) => opt.value === selectedId);
    setForm({
      ...form,
      assigned_doctor_id: selectedId,
      assigned_doctor: selected?.label || "",
    });
    setErrors({ ...errors, assigned_doctor_id: "" });
  };

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setErrors({ ...errors, [field]: "" }); // clear error when typing
  };

  const validate = () => validatePatientForm(form);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit(sanitizeFormValues(form));
  };

  return (
    <form onSubmit={handleSubmit} className="p-5">
      <StepHeader
        title={patient?.id ? "Edit Patient" : "Create New Patient"}
        onClose={() => navigate("/")}
      />
      <div>
        <InputField
          label="First name"
          required
          value={form.first_name}
          onChange={update("first_name")}
          placeholder="first name"
          error={errors.first_name}
        />
        <InputField
          label="Last name"
          required
          value={form.last_name}
          onChange={update("last_name")}
          placeholder="last name"
          error={errors.last_name}
        />
        <InputField
          label="Email ID"
          required
          value={form.email}
          onChange={update("email")}
          placeholder="patientname@gmail.com"
          error={errors.email}
        />
        <PhoneField
          label="Phone Number"
          value={form.phone_number}
          onChange={update("phone_number")}
          placeholder="123-456-7890"
        />
        <SelectField
          label="Insurance Provider"
          value={form.insurance_provider_id}
          onChange={handleProviderChange}
          placeholder="Select Provider"
          options={providerOptions}
        />
        <InputField
          label="Policy Number"
          value={form.policy_number}
          onChange={update("policy_number")}
          placeholder="Input text"
        />

        <SelectField
          label="Assigned Doctor"
          value={form.assigned_doctor_id}
          onChange={handleDoctorChange}
          placeholder="Doctor Name"
          options={doctorOptions}
        />
        <div className="pt-4 mt-20">
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
          >
            Save
          </button>
        </div>
      </div>
    </form>
  );
}
